import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Variable, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import contentApi from '@/services/contentApi';
import { agentSchedulesApi } from '@/services/agentSchedulesApi';

interface CreateAgentScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type FrequencyType = 'minutes' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

interface AgentOption {
  id: string;
  name: string;
}

// Detect Jinja2 template variables from content
function detectVariables(content: string): string[] {
  const pattern = /\{\{\s*([\w.]+)\s*\}\}/g;
  const matches = [...content.matchAll(pattern)];
  const variables = new Set<string>();
  for (const match of matches) {
    const fullVar = match[1];
    const rootVar = fullVar.split('.')[0];
    variables.add(rootVar);
  }
  const reserved = ['loop', 'self', 'super', 'true', 'false', 'none', 'True', 'False', 'None'];
  return [...variables].filter(v => !reserved.includes(v));
}

// Days of week for weekly selector
const DAYS_OF_WEEK = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

// Generate cron expression from frequency settings
function generateCron(
  frequency: FrequencyType,
  minute: string,
  hour: string,
  dayOfWeek: string,
  dayOfMonth: string,
  customCron: string
): string {
  switch (frequency) {
    case 'hourly':
      return `${minute} * * * *`;
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekly':
      return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'custom':
      return customCron;
    default:
      return '';
  }
}

// Generate human-readable description
function describeSchedule(
  frequency: FrequencyType,
  intervalMinutes: string,
  minute: string,
  hour: string,
  dayOfWeek: string,
  dayOfMonth: string
): string {
  const time = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  const dayName = DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label || dayOfWeek;

  switch (frequency) {
    case 'minutes':
      return `Every ${intervalMinutes} minute${intervalMinutes === '1' ? '' : 's'}`;
    case 'hourly':
      return `Every hour at :${minute.padStart(2, '0')}`;
    case 'daily':
      return `Daily at ${time}`;
    case 'weekly':
      return `Every ${dayName} at ${time}`;
    case 'monthly':
      return `Monthly on day ${dayOfMonth} at ${time}`;
    case 'custom':
      return 'Custom schedule';
    default:
      return '';
  }
}

export function CreateAgentScheduleDialog({
  open,
  onClose,
  onSuccess,
}: CreateAgentScheduleDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentContent, setAgentContent] = useState<string>('');
  const [loadingContent, setLoadingContent] = useState(false);

  // Form state
  const [agentId, setAgentId] = useState('');
  const [frequency, setFrequency] = useState<FrequencyType>('daily');
  const [intervalMinutes, setIntervalMinutes] = useState('60');
  const [minute, setMinute] = useState('0');
  const [hour, setHour] = useState('9');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [customCron, setCustomCron] = useState('0 9 * * *');
  const [inputVariables, setInputVariables] = useState<Record<string, string>>({});

  // Detect variables from agent content
  const detectedVariables = useMemo(() => {
    return detectVariables(agentContent);
  }, [agentContent]);

  // Generate schedule description
  const scheduleDescription = useMemo(() => {
    return describeSchedule(frequency, intervalMinutes, minute, hour, dayOfWeek, dayOfMonth);
  }, [frequency, intervalMinutes, minute, hour, dayOfWeek, dayOfMonth]);

  useEffect(() => {
    if (open) {
      loadAgents();
      setError(null);
      // Reset form
      setAgentId('');
      setFrequency('daily');
      setIntervalMinutes('60');
      setMinute('0');
      setHour('9');
      setDayOfWeek('1');
      setDayOfMonth('1');
      setCustomCron('0 9 * * *');
      setInputVariables({});
      setAgentContent('');
    }
  }, [open]);

  // Load agent content when selection changes
  useEffect(() => {
    if (agentId) {
      loadAgentContent(agentId);
    } else {
      setAgentContent('');
    }
  }, [agentId]);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const items = await contentApi.listItems({ contentType: 'agent' });
      setAgents(items.map((item: any) => ({ id: item.id, name: item.name })));
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadAgentContent = async (id: string) => {
    setLoadingContent(true);
    try {
      const content = await contentApi.getContent(id);
      setAgentContent(content || '');
    } catch (err) {
      console.error('Failed to load agent content:', err);
      setAgentContent('');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleVariableChange = (name: string, value: string) => {
    setInputVariables(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        agent_id: agentId,
        input_variables: inputVariables,
      };

      if (frequency === 'minutes') {
        payload.interval_minutes = parseInt(intervalMinutes, 10);
      } else {
        payload.cron_expression = generateCron(frequency, minute, hour, dayOfWeek, dayOfMonth, customCron);
      }

      await agentSchedulesApi.createAgentSchedule(payload);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  const isValid = agentId && (
    (frequency === 'minutes' && parseInt(intervalMinutes, 10) > 0) ||
    (frequency === 'custom' && customCron.trim()) ||
    (frequency !== 'minutes' && frequency !== 'custom')
  );

  // Generate hour options (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: String(i),
    label: `${String(i).padStart(2, '0')}:00`,
  }));

  // Generate minute options (0, 15, 30, 45)
  const minuteOptions = [
    { value: '0', label: ':00' },
    { value: '15', label: ':15' },
    { value: '30', label: ':30' },
    { value: '45', label: ':45' },
  ];

  // Generate day of month options (1-28)
  const dayOfMonthOptions = Array.from({ length: 28 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Schedule Agent Run</DialogTitle>
          <DialogDescription>Set up automatic agent execution on a schedule.</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 py-4">
          {/* Agent Selector */}
          <div className="grid gap-2">
            <Label>Agent</Label>
            <Select value={agentId} onValueChange={setAgentId} disabled={loadingAgents}>
              <SelectTrigger>
                <SelectValue placeholder="Select an agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Template Variables */}
          {agentId && detectedVariables.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Variable className="h-4 w-4 text-purple-500" />
                    <Label className="text-sm font-medium">Template Variables</Label>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {detectedVariables.length} detected
                  </Badge>
                </div>
                {loadingContent ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading agent content...
                  </div>
                ) : (
                  <ScrollArea className="max-h-32">
                    <div className="space-y-3 pr-4">
                      {detectedVariables.map((variable) => (
                        <div key={variable} className="space-y-1">
                          <Label className="text-xs font-mono text-muted-foreground">
                            {"{{ "}{variable}{" }}"}
                          </Label>
                          <Input
                            value={inputVariables[variable] || ''}
                            onChange={(e) => handleVariableChange(variable, e.target.value)}
                            placeholder={`Enter value for ${variable}...`}
                            className="h-9 text-sm font-mono"
                          />
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </>
          )}

          <Separator />

          {/* Frequency Selector */}
          <div className="grid gap-2">
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={(v) => setFrequency(v as FrequencyType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Every X minutes</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom cron</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Frequency-specific inputs */}
          {frequency === 'minutes' && (
            <div className="grid gap-2">
              <Label>Run every</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={intervalMinutes}
                  onChange={(e) => setIntervalMinutes(e.target.value)}
                  min={1}
                  max={1440}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            </div>
          )}

          {frequency === 'hourly' && (
            <div className="grid gap-2">
              <Label>At minute</Label>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {minuteOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {frequency === 'daily' && (
            <div className="grid gap-2">
              <Label>Time</Label>
              <div className="flex items-center gap-2">
                <Select value={hour} onValueChange={setHour}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {hourOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={minute} onValueChange={setMinute}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {minuteOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {frequency === 'weekly' && (
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label>Day</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Time</Label>
                <div className="flex items-center gap-2">
                  <Select value={hour} onValueChange={setHour}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={minute} onValueChange={setMinute}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {minuteOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {frequency === 'monthly' && (
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label>Day of month</Label>
                <Select value={dayOfMonth} onValueChange={setDayOfMonth}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOfMonthOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Time</Label>
                <div className="flex items-center gap-2">
                  <Select value={hour} onValueChange={setHour}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={minute} onValueChange={setMinute}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {minuteOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {frequency === 'custom' && (
            <div className="grid gap-2">
              <Label>Cron expression</Label>
              <Input
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                className="font-mono"
                placeholder="0 9 * * 1-5"
              />
              <p className="text-xs text-muted-foreground">
                Format: minute hour day month weekday
              </p>
            </div>
          )}

          {/* Schedule preview */}
          {frequency !== 'custom' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              <Clock className="h-4 w-4" />
              <span>{scheduleDescription}</span>
              <span className="text-xs">(± up to 5 min jitter)</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading || !isValid}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Create Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateAgentScheduleDialog;
